<template>
    <div class="upload-container round-container">
        <div class="upload-wrapper" :class="{ 'hovering' : hovering }" @click="$refs.file.click()">
            <div class="upload-area" @dragover="dragOver" @dragleave="dragLeave" @drop="dropInto">
                <input :id="inputid" type="file" multiple @input="onUpload" ref="file" :accept="acceptedFileTypes" hidden/>
                <div class="label-container">
                    <label>{{ uploadText }}</label>
                    <div class="accepted-file-types" v-if="acceptedFileTypes !== '*'">
                        <br>
                        <label> {{$t('general.file-format-info') + ': ' + acceptedFileTypes }}.</label>
                    </div>
                    <div>
                        <br>
                        <label> {{$t('general.file-size-info') +': max. ' + fileSizeLimitInMb + ' MB.'}}</label>
                    </div>
                </div>
            </div>
        </div>

        <div class="error-container round-container" v-if="typeError || error">
            <label>{{ error || $t('general.wrong-file-format') }}</label>
        </div>

        <div class="file-list-container" v-if="withResultList">
            <label class="title">{{ resultListTitle }}</label>
            <div class="file-container" id="file-container">
                <div class="file" v-for="(file, idx) in modelValue" :key="idx">
                    <div class="file-icon">
                        <img src="@img/file-code-regular.svg"   v-if="showIconForMimeType(file.type, 'code')"  />
                        <img src="@img/file-image-regular.svg"  v-if="showIconForMimeType(file.type, 'image')" />
                        <img src="@img/file-lines-regular.svg"  v-if="showIconForMimeType(file.type, 'lines')" />
                        <img src="@img/file-zipper-regular.svg" v-if="showIconForMimeType(file.type, 'zipper')"/>
                        <img src="@img/file-pdf-regular.svg"    v-if="showIconForMimeType(file.type, 'pdf')"   />
                    </div>

                    <label>{{ file.name }}</label>

                    <div class="remove" @click="removeFile(idx)">
                        <img src="@img/xmark-solid-red.svg" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const ANY_FILE = '*'
const EMIT_UPDATE_MODE_VALUE = 'update:modelValue'
const EMIT_INVALID_TYPE = 'invalidType'
const EMIT_UPLOAD_LIMIT_REACHED = 'uploadLimitReached'
const EMIT_FILE_LIMIT_REACHED = 'fileLimitReached'
const EMIT_SIZE_LIMIT_REACHED = 'sizeLimitReached'

export default {
    name: 'drag-and-drop-upload',
    emits: [ EMIT_UPDATE_MODE_VALUE, EMIT_INVALID_TYPE, EMIT_UPLOAD_LIMIT_REACHED, EMIT_FILE_LIMIT_REACHED, EMIT_SIZE_LIMIT_REACHED ],
    props: {
        modelValue: {
            // array of files
            type: Array,
            required: true,
            default: () => []
        },
        inputid: String,
        uploadText: {
            type: String,
            default: ''
        },
        acceptedFileTypes: {
            type: String,
            default: ANY_FILE,
            example: 'image/png, image, application/pdf, pdf' // comma seperated list of full or partial mime types
            // list of all browser accepted mime-types:
            // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
        },
        uploadLimit: {
            type: Number,
            default: 0 // no limit
        },
        fileLimit: {
            type: Number,
            default: 0 // no limit
        },
        fileSizeLimitInMb: {
            type: Number,
            default: 5
        },
        error: {
            type: String,
            default: undefined
        },
        withResultList: {
            type: Boolean,
            default: true
        },
        resultListTitle: {
            type: String,
            default: 'Uploaded files'
        },
    },
    data() {
        return {
            hovering: false,
            typeError: false
        }
    },
    mounted() {
        const fileListEl = document.getElementById('file-container')
        fileListEl.addEventListener("wheel", (event) => {
            fileListEl.scrollLeft += event.deltaY
            event.preventDefault()
        })
    },
    methods: {
        dragOver(event) {
            event.preventDefault()
            this.hovering = true
        },
        dragLeave() {
            this.hovering = false
        },
        dropInto(event) {
            event.preventDefault();
            this.dragLeave()
            this.newFiles(event.dataTransfer.files)
        },
        onUpload() {
            console.log('input')
            // save the files
            const files = [...this.$refs.file.files]
            // reset the input to avoid the input event not firing
            // when uploading the same file twice
            this.$refs.file.value = ''

            // handle new files
            this.newFiles(files)
        },
        isAcceptedType(fileList) {
            let filesAreAcceptable = true
            const acceptedTypes = this.acceptedFileTypes
                                    .replaceAll(' ', '')
                                    .split(',')

            if (this.acceptedFileTypes !== ANY_FILE) {
                Object.keys(fileList).forEach(fileindex => {
                    let fileAcceptable = false
                    acceptedTypes.forEach(type => {
                        if (fileList[fileindex].type.match(type)) {
                            fileAcceptable = true
                        }
                    })
                    if(!fileAcceptable) {
                            filesAreAcceptable = false
                        }
                })
            }

            return filesAreAcceptable
        },
        removeFile(idx) {
            const tmpValue = [...this.modelValue]
            tmpValue.splice(idx, 1)
            this.$emit(EMIT_UPDATE_MODE_VALUE, tmpValue)
        },
        newFiles(fileList) {
            this.typeError = false

            // handle error cases
            if (this.uploadLimit !== 0 && fileList.length > this.uploadLimit) {
                return this.$emit(EMIT_UPLOAD_LIMIT_REACHED)
            } else if (!this.isAcceptedType(fileList)) {
                this.typeError = true
                return this.$emit(EMIT_INVALID_TYPE)
            }

            // else handle new files
            this.handleUpload(fileList)
        },
        handleUpload(filelist) {
            const fileArray = []

            // filelist may come as an object with files as properties
            if (Array.isArray(filelist)) {
                // if its an array we just transfer the content
                fileArray.push(...filelist)
            } else {
                // else we convert the object to an array for easier handling
                Object.keys(filelist).forEach(idx => {
                    fileArray.push(filelist[idx])
                })
            }
            // use constants to avoid an error
            const modelValuelength = this.modelValue.length
            const fileArraylength = fileArray.length

            // add files to list if there is enough space and if all files are small enough
            const fileLimitReached = (this.fileLimit !== 0) && (modelValuelength + fileArraylength > this.fileLimit)
            const sizeLimitReached = (this.fileSizeLimitInMb !== 0) && fileArray.find(file => file.size > this.fileSizeLimitInMb * 1048576) // mega byte to byte exact

            if (fileLimitReached) {
                // this.limiterror = `Es können maximal ${this.limit} Datei(en) hochgeladen werden!`
                return this.$emit(EMIT_FILE_LIMIT_REACHED, this.fileLimit)
            }

            if (sizeLimitReached) {
                // this.limiterror = `Dateien dürfen maximal ${this.sizeLimitMb} Mb groß sein!`
                return this.$emit(EMIT_SIZE_LIMIT_REACHED, this.fileSizeLimitInMb)
            }

            // else emit old + new list as one value
            this.$emit(EMIT_UPDATE_MODE_VALUE, [ ...this.modelValue, ...fileArray ])
        },
        showIconForMimeType(mimeType, forIconName) {
            let iconName = 'file'

            if (mimeType.startsWith('image/')) mimeType = 'image'

            switch(mimeType) {
                case 'image':
                    iconName = 'image'
                    break
                case 'text/csv':
                case 'application/msword':
                case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                case 'text/plain':
                    iconName = 'lines'
                    break
                case 'text/html':
                case 'application/xhtml+xml':
                case 'application/json':
                case 'application/xml':
                case 'text/xml':
                    iconName = 'code'
                    break
                case 'application/pdf':
                    iconName = 'pdf'
                    break
                case 'application/vnd.rar':
                case 'application/x-tar':
                case 'application/zip':
                case 'application/x-7z-compressed':
                    iconName = 'zipper'
                    break
                default:
                    break
            }

            return iconName === forIconName
        }
    }
}
</script>

<style lang="scss" scoped>
.upload-container {
    width: 100%;

    > .upload-wrapper {
        width: 100%;
        height: 100%;
        padding: 20px;
        border-radius: 8px;

        &:hover,
        &.hovering {
            background: #ddffdd;

            &,
            :deep(*) {
                cursor: pointer;
            }
        }

        > .upload-area {
            height: 100%;
            min-height: 100px;
            border: 2px dashed #333;
            display: flex;
            align-items: center;
            justify-content: center;

            > .label-container {
                font-size: 1.2em;
                margin: 10px;
                text-align: center;
            }
        }
    }

    > .error-container {
        margin-top: 5px;
        color: red;
        padding: 5px;
    }

    > .file-list-container {
        margin-top: 20px;
        padding: 8px;
        width: 100%;
        border-radius: 8px;
        height: 130px;
        box-shadow: 0 2px 8px #00000022;

        ::-webkit-scrollbar {
            height: 6px;
            width: 6px;
        }

        ::-webkit-scrollbar-track {
            border-radius: 8px;
            background-color: #eee;
        }

        ::-webkit-scrollbar-thumb {
            border-radius: 8px;
            background-color: #999;
        }

        > .title {
            font-weight: bold;
        }

        > .file-container {
            height: 85px;
            display: flex;
            overflow-x: auto;
            overflow-y: hidden;
            margin-top: 10px;
            padding-bottom: 5px;

            > .file {
                height: 100%;
                align-items: center;
                display: flex;
                flex-direction: column;
                min-width: 100px;
                max-width: 100px;
                margin-right: 10px;
                padding: 4px;
                border-radius: 8px;

                pointer-events: none;
                &:hover {
                    background: #00000011;
                    &, :deep(*) {
                        cursor: pointer;
                    }
                }

                > .file-icon {
                    height: 40%;

                    > img {
                        height: 100%;
                    }
                }

                > label {
                    height: 30%;
                    max-width: 100%;
                    display: block;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    margin-top: 5px;
                }

                > .remove {
                    height: 30%;
                    width: 100%;
                    fill: red;
                    margin-top: 5px;
                    pointer-events: auto;

                    &:hover {
                        cursor: pointer;
                    }

                    > img {
                        height: 100%;
                        margin: 0 auto;
                        display: block;
                    }
                }
            }
        }
    }
}
</style>